import React, { useState } from "react";
import { teamIdentity, registerTeamCrest } from "@/presentation/teamIdentity";
import { countryFlagUrl } from "@/presentation/countryFlags";

// Renders a REAL team/league logo only when the backend actually provides a valid
// http(s) URL. Never guesses a logo from the name, never searches externally.
// Falls back to an initials avatar; onError also falls back (no broken image icon).
export function TeamLogo({ src, name, size = 28, rounded = "rounded-md" }: { src?: string | null; name?: string; size?: number; rounded?: string }) {
  const [failed, setFailed] = useState(false);
  const show = !!src && !failed;
  const initials = (name || "?").trim().charAt(0).toUpperCase() || "?";
  if (show) {
    return (
      <img src={src as string} alt={name || ""} loading="lazy" onError={() => setFailed(true)}
        className={`shrink-0 ${rounded} bg-white/5 object-contain`} style={{ width: size, height: size }} />
    );
  }
  return (
    <span className={`grid shrink-0 place-items-center ${rounded} bg-white/[0.08] font-bold text-foreground/70`} style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}>
      {initials}
    </span>
  );
}

// Tolerant extraction of a real logo URL from a backend object. Accepts both the
// flat keys (home_logo, league_logo, …) and nested provider shapes
// (home.logo, teams.home.logo, fixture.teams.home.logo, league.logo, …). Only
// absolute http(s) URLs are accepted so a missing/garbage value cleanly yields
// the initials fallback. Never guesses a logo from the name.
export function logoFrom(obj: any, side: "home" | "away" | "league" | "" = ""): string | null {
  if (!obj || typeof obj !== "object") return null;
  const abs = (v: any): string | null => (typeof v === "string" && /^https?:\/\//i.test(v) ? v : null);
  const t = obj.teams || obj.fixture?.teams;
  let candidates: any[] = [];
  if (side === "home") {
    candidates = [obj.home_logo, obj.home_team_logo, obj.homeLogo, obj.home_badge, obj.home_crest, obj.homeCrest, obj.home_image, obj.homeImage, obj.home?.logo, t?.home?.logo];
  } else if (side === "away") {
    candidates = [obj.away_logo, obj.away_team_logo, obj.awayLogo, obj.away_badge, obj.away_crest, obj.awayCrest, obj.away_image, obj.awayImage, obj.away?.logo, t?.away?.logo];
  } else if (side === "league") {
    candidates = [obj.league_logo, obj.leagueLogo, obj.competition_logo, obj.competitionLogo, obj.league_image, obj.league_badge, obj.league?.logo, obj.fixture?.league?.logo];
  } else {
    candidates = [obj.team_logo, obj.logo, obj.crest, obj.badge, obj.image];
  }
  for (const c of candidates) { const v = abs(c); if (v) return v; }
  return null;
}

// ONE shared team-visual resolution path — used by every live/prematch card and
// the match-detail hero so visuals can never drift between surfaces. Order:
//   1) authoritative current-state logo  2) signal logo  3) prematch/other logo
//   (any provider objects passed in priority order), then
//   4) existing team-crest resolver (teamIdentity), then
//   5) existing national-team flag resolver (countryFlagUrl), then
//   null -> TeamLogo renders the initials avatar. Never invents a URL.
export function resolveTeamVisual(name: string | null | undefined, side: "home" | "away", sources: any[]): string | null {
  for (const o of sources) {
    const v = logoFrom(o, side);
    if (v) {
      // Cache real crests by team name so every surface (card/detail) shares the
      // SAME local visual index — a crest seen on one page shows on the others.
      if (name) registerTeamCrest(name, v);
      return v;
    }
  }
  if (name) {
    const crest = teamIdentity(name).crestUrl;
    if (crest) return crest;
    const flag = countryFlagUrl(name);
    if (flag) return flag;
  }
  return null;
}
